import { INoRowsOverlayAngularComp } from '@ag-grid-community/angular';
import { INoRowsOverlayParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, computed, HostBinding, inject } from '@angular/core';

import { CustomCurvesQuery } from '@models/custom-curves/custom-curves.query';
import { CustomCurve } from '@models/custom-curves/custom-curves.state';

import { ButtonComponent } from '@shared/components/button/button.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { PermissionType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { WorkflowStepType } from '@shared/store/workflow/workflow.const';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

import { CustomDriverCreateEditModalComponent } from '../custom-driver-create-edit-modal/custom-driver-create-edit-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, TooltipDirective],
  selector: 'aux-custom-curves-no-rows-overlay',
  styleUrl: './custom-curves-no-rows-overlay.component.scss',
  templateUrl: './custom-curves-no-rows-overlay.component.html',
})
export class CustomCurvesNoRowsOverlayComponent implements INoRowsOverlayAngularComp {
  readonly customCurvesQuery = inject(CustomCurvesQuery);

  private readonly authService = inject(AuthService);
  private readonly overlayService = inject(OverlayService);
  private readonly workflowQuery = inject(WorkflowQuery);

  curves = computed<CustomCurve[]>(() => {
    return this.customCurvesQuery.curves() || [];
  });

  isUserAuthorized = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_MODIFY_CUSTOM_CURVE],
    sysAdminsOnly: false,
  });
  isWFLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStepType.WF_STEP_MONTH_CLOSE_LOCK_CURVES
  );

  hasModifyCustomCurvePermission = computed(() => {
    return this.isUserAuthorized() && !this.isWFLocked();
  });
  noPermTooltip = computed(() => {
    return !this.isUserAuthorized()
      ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
      : this.isWFLocked()
        ? MessagesConstants.SITE_PATIENT_CUSTOM_CURVES_LOCKED_TEXT
        : '';
  });

  params!: INoRowsOverlayParams;

  agInit(params: INoRowsOverlayParams): void {
    this.params = params;
  }

  @HostBinding('style.pointerEvents') get events(): string {
    return 'all';
  }

  onCreateButtonClick(): void {
    type InstanceType = CustomDriverCreateEditModalComponent | null;
    this.overlayService.openPopup({
      content: CustomDriverCreateEditModalComponent,
      data: {
        existingNames: this.curves().map((curve) => curve.name),
      },
      settings: {
        header: 'Create a New Driver Curve',
        primaryButton: {
          action: (instance: InstanceType) => instance?.onSave(),
          disabled: (instance: InstanceType) => !!instance?.saveDisabled,
          label: 'Create Curve',
          pendoTag: 'custom-curves-create-modal-confirm-button',
        },
        secondaryButton: { pendoTag: 'custom-curves-create-modal-cancel-button' },
      },
    });
  }
}
